import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  usersApi,
  type AdjustXpBody,
  type BypassPrerequisiteBody,
  type ForceEnqueueBody,
  type GrantAccessoryBody,
  type ListUsersParams,
  type SendNotificationBody,
  type SoftDeleteBody,
  type ToggleHolidayBody,
} from '../api/usersApi';
import { usersKeys } from '../queryKeys';
import { humanizeError } from '@/lib/apiClient';
import { toast } from '@/hooks/useToast';

export function useUsersList(params: ListUsersParams) {
  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: () => usersApi.list(params),
    staleTime: 30_000,
  });
}

export function useUserDetail(userId: string | undefined) {
  return useQuery({
    queryKey: usersKeys.detail(userId ?? ''),
    queryFn: () => usersApi.detail(userId as string),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useUserGoals(userId: string | undefined) {
  return useQuery({
    queryKey: usersKeys.goals(userId ?? ''),
    queryFn: () => usersApi.goals(userId as string),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useUserGoalPhases(
  userId: string | undefined,
  goalId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: usersKeys.goalPhases(userId ?? '', goalId ?? ''),
    queryFn: () => usersApi.goalPhases(userId as string, goalId as string),
    enabled: !!userId && !!goalId && enabled,
    staleTime: 60_000,
  });
}

export function usePhaseTasks(
  userId: string | undefined,
  goalId: string | undefined,
  phaseId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: usersKeys.phaseTasks(userId ?? '', goalId ?? '', phaseId ?? ''),
    queryFn: () =>
      usersApi.phaseTasks(userId as string, goalId as string, phaseId as string),
    enabled: !!userId && !!goalId && !!phaseId && enabled,
    staleTime: 60_000,
  });
}

export function useUserAccessories(userId: string | undefined) {
  return useQuery({
    queryKey: usersKeys.accessories(userId ?? ''),
    queryFn: () => usersApi.accessories(userId as string),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useUserNotificationState(userId: string | undefined) {
  return useQuery({
    queryKey: usersKeys.notificationState(userId ?? ''),
    queryFn: () => usersApi.notificationState(userId as string),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useUserCheckins(userId: string | undefined, limit = 30) {
  return useQuery({
    queryKey: [...usersKeys.checkins(userId ?? ''), { limit }] as const,
    queryFn: () => usersApi.checkins(userId as string, { limit }),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useUserPointsHistory(userId: string | undefined, limit = 20) {
  return useQuery({
    queryKey: [...usersKeys.pointsHistory(userId ?? ''), { limit }] as const,
    queryFn: () => usersApi.pointsHistory(userId as string, limit),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

export function useUserCheckinHeatmap(userId: string | undefined, days = 90) {
  return useQuery({
    queryKey: [...usersKeys.checkinHeatmap(userId ?? ''), { days }] as const,
    queryFn: () => usersApi.checkinHeatmap(userId as string, days),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });
}

function invalidateUserDetail(qc: ReturnType<typeof useQueryClient>, userId: string) {
  qc.invalidateQueries({ queryKey: usersKeys.detail(userId) });
  qc.invalidateQueries({ queryKey: usersKeys.lists() });
}

export function useAdjustXp(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdjustXpBody) => usersApi.adjustXp(userId, body),
    onSuccess: () => {
      invalidateUserDetail(qc, userId);
      toast({ title: 'XP adjusted', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'XP adjust failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useToggleHoliday(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ToggleHolidayBody) => usersApi.toggleHoliday(userId, body),
    onSuccess: (_d, vars) => {
      invalidateUserDetail(qc, userId);
      toast({
        title: vars.enable ? 'Holiday enabled' : 'Holiday cleared',
        variant: 'success',
      });
    },
    onError: (err) =>
      toast({ title: 'Holiday toggle failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useGrantAccessory(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GrantAccessoryBody) => usersApi.grantAccessory(userId, body),
    onSuccess: (_d, vars) => {
      invalidateUserDetail(qc, userId);
      toast({
        title: vars.action === 'grant' ? 'Accessory granted' : 'Accessory revoked',
        variant: 'success',
      });
    },
    onError: (err) =>
      toast({ title: 'Accessory action failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useSoftDeleteUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SoftDeleteBody) => usersApi.softDelete(userId, body),
    onSuccess: () => {
      invalidateUserDetail(qc, userId);
      toast({ title: 'User soft-deleted', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Delete failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useSendUserNotification(userId: string) {
  return useMutation({
    mutationFn: (body: SendNotificationBody) => usersApi.sendNotification(userId, body),
    onSuccess: () => toast({ title: 'Notification queued', variant: 'success' }),
    onError: (err) =>
      toast({ title: 'Notification failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useBypassPrerequisite(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BypassPrerequisiteBody) => usersApi.bypassPrerequisite(userId, body),
    onSuccess: () => {
      invalidateUserDetail(qc, userId);
      toast({ title: 'Prerequisite bypassed', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Bypass failed', description: humanizeError(err), variant: 'destructive' }),
  });
}

export function useForceEnqueue(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ForceEnqueueBody) => usersApi.forceEnqueue(userId, body),
    onSuccess: () => {
      invalidateUserDetail(qc, userId);
      toast({ title: 'User enqueued for matching', variant: 'success' });
    },
    onError: (err) =>
      toast({ title: 'Enqueue failed', description: humanizeError(err), variant: 'destructive' }),
  });
}
